// K8 (Finar 2026-10-28) — kısmi toplam (†) hücresinin "neden kısmi" bilgisi: atlanan (Ölçülmüyor)
// gösterge kodları → etiket, atlanan işletme kimlikleri → ad. Izgara ⓘ penceresi (PartialInfoPopover)
// ve FormulaPopover "Ölçülmeyen" bloğu bu tek yardımcıdan okur.
// Portal: müşteriye kapalı girdi kodu sunucuda `gizli_N`'e maskelenmiş gelir (`maskCellRefs`); ızgarada
// bulunmayan her kod da kapalı sayılır — ne kod ne ad gösterilir (çağıran `hiddenInput` yazar).
// Danışman: bulunmayan kod ham kodla yazılır (sette olmayan eski girdi; sızıntı riski yok).

import type { KpiResolvedCell, KpiResolvedGrid } from "@mahir/shared";
import { KPI_COPY } from "./kpi-ui";

/** Sunucu maskesinin yer tutucusu (client-formula-mask.ts) — kapalı girdinin kimliği değildir. */
const HIDDEN_PLACEHOLDER_RE = /^gizli_\d+$/;

/** `gizli_N` maske yer tutucusu mu (popover'ın yedek savunması da bunu kullanır). */
export function isMaskPlaceholder(code: string): boolean {
  return HIDDEN_PLACEHOLDER_RE.test(code);
}

export interface PartialLookup {
  labelByCode: ReadonlyMap<string, string>;
  nameByEntity: ReadonlyMap<string, string>;
}

export interface PartialRefItem {
  code: string;
  /** görünür etiket; `hidden` ise boş — çağıran kapalı girdi metnini yazar */
  label: string;
  hidden: boolean;
}

export interface PartialInfo {
  refs: PartialRefItem[];
  entities: string[];
  hasPending: boolean;
}

export function buildPartialLookup(
  grid: Pick<KpiResolvedGrid, "indicators" | "entities">,
): PartialLookup {
  return {
    labelByCode: new Map(grid.indicators.map((i) => [i.code, i.label])),
    nameByEntity: new Map(grid.entities.map((e) => [e.id, e.name])),
  };
}

/** Kod müşteriye kapalı mı: maske yer tutucusu, açık kapalı listesi ya da (portalda) ızgarada yok. */
export function isHiddenPartialRef(
  code: string,
  lookup: PartialLookup,
  opts: { client: boolean; hiddenInputs?: readonly string[] | undefined },
): boolean {
  if (isMaskPlaceholder(code)) return true;
  if (opts.hiddenInputs?.includes(code)) return true;
  return opts.client && !lookup.labelByCode.has(code);
}

export function partialInfoFor(
  cell: KpiResolvedCell | undefined,
  lookup: PartialLookup,
  opts: { client: boolean; hiddenInputs?: readonly string[] | undefined },
): PartialInfo {
  const refs = (cell?.partialRefs ?? []).map((code): PartialRefItem => {
    const hidden = isHiddenPartialRef(code, lookup, opts);
    return { code, hidden, label: hidden ? "" : (lookup.labelByCode.get(code) ?? code) };
  });
  // portalda bilinmeyen işletme kimliği ham basılmaz (kapsam zaten süzer; savunma) — nötr metin
  const unknownEntity = (id: string): string =>
    opts.client ? KPI_COPY.portal.notes.hiddenInput : id;
  const entities = (cell?.partialEntityIds ?? []).map(
    (id) => lookup.nameByEntity.get(id) ?? unknownEntity(id),
  );
  return { refs, entities, hasPending: cell?.hasPending === true };
}

/**
 * ⓘ düğmesinde ızgaraya köpürmeyecek tuşlar: etkinleştirme (Enter/Space — tablo Enter'da
 * preventDefault + formül penceresi, Space'te editör açar) ve pencere açıkken Escape. Ok/Home/End/
 * Tab köpürür → ızgara gezinmesi odak ⓘ'deyken de çalışır.
 */
export function partialInfoKeyStops(key: string, open: boolean): boolean {
  return key === "Enter" || key === " " || (key === "Escape" && open);
}
